import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, TrendingUp, Award, Target } from 'lucide-react';
import { metricTile } from '@/lib/metricTileStyles';

interface CareerAnniversaryInsightsProps {
  occupationTitle: string;
  currentSalary?: number;
  lastYearSalary?: number;
  industryGrowth?: number;
  userStartDate?: string; // When user first viewed this occupation
  className?: string;
}

const CareerAnniversaryInsights: React.FC<CareerAnniversaryInsightsProps> = ({
  occupationTitle,
  currentSalary = 0,
  lastYearSalary = 0,
  industryGrowth = 0,
  userStartDate,
  className = ''
}) => {
  const yoyChange = lastYearSalary > 0 ? ((currentSalary - lastYearSalary) / lastYearSalary) * 100 : 0;
  const outperformance = yoyChange - industryGrowth;
  
  // Calculate monitoring period
  const getMonitoringPeriod = () => {
    if (!userStartDate) return null;
    const start = new Date(userStartDate);
    const now = new Date();
    const monthsDiff = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
    
    if (monthsDiff >= 12) {
      const years = Math.floor(monthsDiff / 12);
      return `${years} year${years > 1 ? 's' : ''}`;
    }
    return `${monthsDiff} month${monthsDiff > 1 ? 's' : ''}`;
  };

  const monitoringPeriod = getMonitoringPeriod();

  return (
      <Card className={`shadow-lg ${className}`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            Your Career Performance Report
            {monitoringPeriod && (
              <Badge variant="secondary" className="ml-auto">
                {monitoringPeriod} tracking
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Performance Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`p-4 ${metricTile.green.surface}`}>
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className={`h-4 w-4 ${metricTile.green.icon}`} />
                <Badge variant="outline" className={metricTile.green.badge}>
                  YoY Growth
                </Badge>
              </div>
              <div className={`text-2xl font-bold ${metricTile.green.value}`}>
                {yoyChange >= 0 ? '+' : ''}{yoyChange.toFixed(1)}%
              </div>
              <div className={`text-sm ${metricTile.green.label}`}>Salary increase</div>
            </div>

            <div className={`p-4 ${metricTile.blue.surface}`}>
              <div className="flex items-center justify-between mb-2">
                <Target className={`h-4 w-4 ${metricTile.blue.icon}`} />
                <Badge variant="outline" className={metricTile.blue.badge}>
                  vs Industry
                </Badge>
              </div>
              <div className={`text-2xl font-bold ${metricTile.blue.value}`}>
                {outperformance >= 0 ? '+' : ''}{outperformance.toFixed(1)}%
              </div>
              <div className={`text-sm ${metricTile.blue.label}`}>Outperformance</div>
            </div>

            <div className={`p-4 ${metricTile.purple.surface}`}>
              <div className="flex items-center justify-between mb-2">
                <Calendar className={`h-4 w-4 ${metricTile.purple.icon}`} />
                <Badge variant="outline" className={metricTile.purple.badge}>
                  Current
                </Badge>
              </div>
              <div className={`text-xl font-bold ${metricTile.purple.value}`}>
                ${currentSalary.toLocaleString()}
              </div>
              <div className={`text-sm ${metricTile.purple.label}`}>Market position</div>
            </div>
          </div>

          {/* Insights & Recommendations */}
          <div className="space-y-4">
            <h4 className="font-semibold">Your Career Anniversary Insights</h4>
            
            <div className="space-y-3">
              <div className="p-3 bg-muted/20 rounded-lg">
                <h5 className="font-medium text-sm mb-1 flex items-center gap-2">
                  <TrendingUp className={`h-4 w-4 ${metricTile.green.icon}`} />
                  Performance Summary
                </h5>
                <p className="text-sm text-muted-foreground">
                  {yoyChange > industryGrowth 
                    ? `Excellent progress! Your ${occupationTitle} career is outperforming the industry average by ${outperformance.toFixed(1)} percentage points.`
                    : yoyChange > 0 
                    ? `Your ${occupationTitle} career shows positive growth, though slightly below industry average. Consider skill development to close the gap.`
                    : `Market conditions have been challenging for ${occupationTitle}. Focus on defensive positioning and skill diversification.`
                  }
                </p>
              </div>

              <div className="p-3 bg-muted/20 rounded-lg">
                <h5 className="font-medium text-sm mb-1 flex items-center gap-2">
                  <Target className={`h-4 w-4 ${metricTile.blue.icon}`} />
                  Strategic Recommendations
                </h5>
                <p className="text-sm text-muted-foreground">
                  {outperformance > 5 
                    ? `You're in the top tier of ${occupationTitle} professionals. Consider leadership roles or specialized expertise to maintain momentum.`
                    : outperformance > 0
                    ? `Solid performance positions you well for advancement. Focus on high-impact projects and network expansion.`
                    : `Bridge the performance gap with targeted skill development, certification programs, or market repositioning strategies.`
                  }
                </p>
              </div>

              <div className="p-3 bg-muted/20 rounded-lg">
                <h5 className="font-medium text-sm mb-1 flex items-center gap-2">
                  <Calendar className={`h-4 w-4 ${metricTile.purple.icon}`} />
                  Next Review Milestone
                </h5>
                <p className="text-sm text-muted-foreground">
                  {monitoringPeriod 
                    ? `After ${monitoringPeriod} of tracking, consider quarterly reviews to optimize career trajectory and market positioning.`
                    : `Set up quarterly performance reviews to track progress against ${occupationTitle} market trends and opportunities.`
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Action Items */}
          <div className="p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border-l-4 border-primary">
            <h5 className="font-semibold text-primary mb-2">Recommended Actions</h5>
            <ul className="text-sm space-y-1">
              {outperformance > 0 && (
                <li>• Leverage strong performance for salary negotiation or promotion discussions</li>
              )}
              <li>• Monitor quarterly trends to identify optimal timing for career moves</li>
              <li>• Explore emerging skills in high-growth areas of {occupationTitle}</li>
              <li>• Set alerts for market changes that could impact your career trajectory</li>
            </ul>
          </div>
        </CardContent>
      </Card>
  );
};

export default CareerAnniversaryInsights;
